// src/dev/devSimulated.ts
//
// Dev-only affordance for exercising the "simulated" markets path when the
// frontend talks to the indexer DIRECTLY (no apiProxy in the loop). In that
// mode nothing sets the X-Allow-Simulated header from the user's role, so this
// module lets a developer toggle it by hand.
//
// This is INERT in production builds and adds NO attack surface:
//   - import.meta.env.DEV is false in `vite build`, so every export no-ops and
//     the toggle widget renders nothing.
//   - X-Allow-Simulated is client-controllable by anyone with curl already;
//     setting it here grants no capability an attacker doesn't have. The
//     deployed apiProxy strips any client-supplied copy and re-derives the flag
//     from the Firestore role (see functions/lib/apiAccess.js buildForwardHeaders),
//     so this can never escalate privileges on the proxied path.
//
// All dev-only logic lives here; shipping code touches it through a single
// seam (devSimulatedHeaders() in the indexer api client).

const STORAGE_KEY = 'irm.dev.simulated';
const HEADER = 'x-allow-simulated';

// True only in a Vite dev build. Statically false (and tree-shaken) in prod.
function devEnabled(): boolean {
    return import.meta.env.DEV === true;
}

// Initial state when nothing has been toggled yet, seeded from the env var.
function seed(): boolean {
    const v = import.meta.env.VITE_ALLOW_SIMULATED;
    return v === '1' || v === 'true';
}

/** Whether direct-to-indexer requests should carry the simulated flag. */
export function isSimulatedEnabled(): boolean {
    if (!devEnabled()) return false;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored === null ? seed() : stored === '1';
    } catch {
        // No localStorage (e.g. node test env) — fall back to the env seed.
        return seed();
    }
}

/** Persist the toggle. No-op outside a dev build. */
export function setSimulatedEnabled(on: boolean): void {
    if (!devEnabled()) return;
    try {
        localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch {
        // Ignore: persistence is best-effort in environments without storage.
    }
}

/** Headers to merge into outgoing indexer requests. Empty unless dev + enabled. */
export function devSimulatedHeaders(): Record<string, string> {
    return isSimulatedEnabled() ? { [HEADER]: '1' } : {};
}
